import {
    useClickOutside,
    useDropdownKeyboard,
} from "@mrflark/core/hooks";
import {
    useQuery,
} from "@tanstack/react-query";
import type {
    Ref,
    RefObject,
} from "preact";
import {
    useEffect,
    useRef,
    useState,
} from "preact/compat";
import {
    splClient,
} from "../../api/client.ts";

type TagInputProps = {
    onAddTag: (tag: string) => void;
    existingTags?: string[];
    placeholder?: string;
    inputRef?: RefObject<HTMLInputElement | null>;
    // controls dropdown direction relative to the input. defaults to "bottom".
    // use "top" when the input lives inside a fixed-position container near the
    // bottom of the viewport (e.g. BulkActionsBar) to prevent the dropdown from
    // overflowing off-screen.
    placement?: "top" | "bottom";
};

export const TagInput = ({
    onAddTag,
    existingTags = [],
    placeholder = "Add custom tag...",
    inputRef: externalRef,
    placement = "bottom",
}: TagInputProps) => {
    const [value, setValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = externalRef || internalRef;
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch suggestions with debounce
    const {
        data: suggestionsData,
    } = useQuery({
        queryKey: ["tagSuggestions", value],
        queryFn: () => splClient.getTagSuggestions(value, 10),
        enabled: value.length > 0,
        staleTime: 30000,
    });

    // Filter out already applied tags
    const suggestions = suggestionsData?.suggestions.filter(
        (s) => !existingTags.includes(s.tag),
    ) || [];

    // Show "Create new tag" option when input doesn't exactly match any suggestion
    const trimmedValue = value.trim().toLowerCase();
    const showCreateOption = trimmedValue.length > 0 &&
        !existingTags.includes(trimmedValue) &&
        !suggestions.some((s) => s.tag.toLowerCase() === trimmedValue);

    // Total selectable items: suggestions + optional create option
    const totalOptions = suggestions.length + (showCreateOption ? 1 : 0);

    // Reset selected index when suggestions change
    useEffect(() => {
        setSelectedIndex(0);
    }, [suggestions.length, showCreateOption]);

    useClickOutside([inputRef, dropdownRef], () => setIsOpen(false));

    const handleSubmit = (tag?: string) => {
        const tagToAdd = (tag || value).toLowerCase().trim();
        if (tagToAdd && !existingTags.includes(tagToAdd)) {
            onAddTag(tagToAdd);
            setValue("");
            setIsOpen(false);
        }
    };

    const selectOption = (index: number) => {
        // "create new tag" option is the last item when showCreateOption is true
        if (showCreateOption && index === suggestions.length) {
            handleSubmit();
        } else if (suggestions[index]) {
            handleSubmit(suggestions[index].tag);
        } else {
            handleSubmit();
        }
    };

    const {
        handleKeyDown,
    } = useDropdownKeyboard({
        isOpen,
        totalOptions,
        selectedIndex,
        setSelectedIndex,
        onSelect: selectOption,
        onClose: () => setIsOpen(false),
        onSubmitWhenClosed: () => handleSubmit(),
    });

    return (
        <div className="relative">
            <input
                // preact/compat's Ref<HTMLInputElement> type doesn't
                // accept the | null variant returned by useRef(null);
                // cast away the nullable since the runtime accepts it
                ref={inputRef as Ref<HTMLInputElement>}
                type="text"
                value={value}
                onChange={(e) => {
                    setValue((e.target as HTMLInputElement).value);
                    setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="input w-full"
            />

            {isOpen && totalOptions > 0 && (
                <div
                    ref={dropdownRef}
                    data-testid="tag-input-dropdown"
                    className={`absolute z-50 w-full bg-bg-secondary border border-border rounded-md shadow-lg max-h-60 overflow-auto ${
                        placement === "top" ? "bottom-full mb-1" : "mt-1"
                    }`}
                >
                    {suggestions.map((suggestion, index) => (
                        <button
                            type="button"
                            key={suggestion.tag}
                            onClick={() => handleSubmit(suggestion.tag)}
                            className={`
                  w-full px-3 py-2 text-left flex items-center justify-between
                  ${
                                index === selectedIndex
                                    ? "bg-bg-tertiary"
                                    : "hover:bg-bg-tertiary"
                            }
                  transition-colors
                `}
                        >
                            <span className="text-text-primary">
                                {highlightMatch(suggestion.tag, value)}
                            </span>
                            <span className="text-text-muted text-sm">
                                {suggestion.count}
                            </span>
                        </button>
                    ))}
                    {showCreateOption && (
                        <button
                            type="button"
                            onClick={() => handleSubmit()}
                            className={`
                w-full px-3 py-2 text-left flex items-center justify-between
                border-t border-border
                ${
                                selectedIndex === suggestions.length
                                    ? "bg-bg-tertiary"
                                    : "hover:bg-bg-tertiary"
                            }
                transition-colors
              `}
                        >
                            <span className="text-text-secondary">
                                Create "<span className="text-accent">
                                    {trimmedValue}
                                </span>"
                            </span>
                            <span className="text-text-muted text-sm">
                                + new
                            </span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Helper to highlight matching text
const highlightMatch = (text: string, query: string) => {
    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    return (
        <>
            {text.slice(0, index)}
            <strong className="font-bold">
                {text.slice(index, index + query.length)}
            </strong>
            {text.slice(index + query.length)}
        </>
    );
};
